'use client'

import { YStack } from '@lyra/core/components'
import BodyText from '@lyra/core/components/BodyText'
import Section from '@lyra/core/components/Section'
import formatDate from '@lyra/core/utils/formatDate'
import PageWithBanner from '@lyra/web/components/layout/PageWithBanner'
import { styled } from 'tamagui'

const UPDATED_TIMESTAMP = 1726885344

const OrderedList = styled(YStack, {
  tag: 'ol',
  marginLeft: '$2',
  style: { display: 'grid' },
})

const SectionOne = () => {
  return (
    <Section isLarge noTopBorder>
      <Section.Header title="1. These Terms of Use" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> The Derive Protocol is a crypto asset options and derivatives
            trading protocol that is operated and governed by the Derive decentralised autonomous
            organisation (Derive DAO).
          </BodyText>
          <BodyText>
            <strong>b.</strong> Lyra Technologies Corp operates the website-hosted application
            through which you access the Derive Protocol. The components of the Application consist
            of the offchain orderbook, the technology infrastructure that supports third-party
            hosted wallets, and a user interface. The Application is hosted at www.derive.xyz. The
            Application is one way, but not the only way, to access the Derive Protocol.
          </BodyText>
          <BodyText>
            <strong>c.</strong> These terms of use (Terms) govern your access to and use of the
            Application. You must read these Terms carefully.
          </BodyText>
          <BodyText>
            <strong>d.</strong> In these Terms:
            <OrderedList>
              <BodyText>
                <i>i.</i> “we”, “us”, or “our” refers to Lyra Technologies Corp; and
              </BodyText>
              <BodyText>
                <i>ii.</i> “you”, “your”, or “user(s)” refers to anyone who accesses or uses the
                Application in any way. If you are accessing or using the Application on behalf of a
                company or other legal entity, you represent and warrant that you have the authority
                to bind that entity to these Terms, and in such case “you”, “your”, or “user(s)”
                will refer to that entity.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>e.</strong> By accessing, browsing, or otherwise using any part of the
            Application or by acknowledging acceptance or agreement to these Terms on the
            Application, you agree to be bound by these Terms. You must not use the Application if
            you do not agree to these Terms.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionTwo = () => {
  return (
    <Section isLarge>
      <Section.Header title="2. Eligibility" />
      <Section.YStack>
        <BodyText>To access and use the Application, you must:</BodyText>
        <OrderedList>
          <li>
            <BodyText>
              <strong>a.</strong> If you are an individual, be at least 18 years of age or the age
              at which a person is taken to have capacity to contract in the jurisdiction where you
              reside;
            </BodyText>
          </li>
          <li>
            <BodyText>
              <strong>b.</strong> If you are accessing or using the Application on behalf of a
              company or other legal entity, have all power and authority to bind that entity;
            </BodyText>
          </li>
          <li>
            <BodyText>
              <strong>c.</strong> Be legally capable of entering into binding contracts;
            </BodyText>
          </li>
          <li>
            <BodyText>
              <strong>d.</strong> Have the capacity to meet all of your obligations under these
              Terms;
            </BodyText>
          </li>
          <li>
            <BodyText>
              <strong>e.</strong> Not have been previously suspended or removed from accessing or
              using the Application;
            </BodyText>
          </li>{' '}
          <li>
            <BodyText>
              <strong>f.</strong> Not be an Australian or United States of America tax resident; and
            </BodyText>
          </li>
          <li>
            <BodyText>
              <strong>g.</strong> Ensure the representations and warranties set out in section 3.1
              remain true and accurate at all times.
            </BodyText>
          </li>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionThree = () => {
  return (
    <Section isLarge>
      <Section.Header title="3. Your Access to the Application" />
      <Section.Subheader title="3.1 Your representations and warranties" />
      <Section.YStack>
        <BodyText>
          By accessing and using the Application you represent and warrant on a continuing basis
          that:
        </BodyText>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> You meet the eligibility criteria set out in clause 2;
          </BodyText>
          <BodyText>
            <strong>b.</strong> You are not:
            <br />
            <OrderedList>
              <li>
                <i>i.</i> A Sanctioned Person; or
              </li>
              <li>
                <i>ii.</i> A Restricted Person;
              </li>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>c.</strong> You are not accessing or using the Application on behalf of a
            Sanctioned Person or Restricted Person;
          </BodyText>
          <BodyText>
            <strong>d.</strong> You do not intend to transact with any Restricted Person or
            Sanctioned Person;
          </BodyText>
          <BodyText>
            <strong>e.</strong> You do not and will not use a virtual private network (VPN) or any
            other privacy or anonymisation tools or techniques to circumvent or attempt to
            circumvent any restrictions that apply to the Application; and
          </BodyText>
          <BodyText>
            <strong>f.</strong> Your access and use of the Application does not breach any
            Applicable Law or facilitate any activity that could breach any Applicable Law.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="3.2 Acknowledgements" />
      <Section.YStack>
        <BodyText>
          By accessing and using the Application, you acknowledge, understand, and agree that:
          <OrderedList>
            <BodyText>
              <strong>a.</strong>
              The Application may be inaccessible or inoperable for any reason from time to time,
              including due to:
              <OrderedList>
                <BodyText>i. equipment, hardware or software malfunction;</BodyText>
                <BodyText>
                  ii. periodic maintenance procedures or repairs that we or any of our service
                  providers may undertake from time to time;
                </BodyText>
                <BodyText>
                  iii. causes beyond our control or that we cannot reasonably foresee;
                </BodyText>
                <BodyText>
                  iv. disruptions and temporary or permanent unavailability of underlying blockchain
                  infrastructure; or
                </BodyText>
                <BodyText>
                  v. unavailability of third party service providers or external partners for any
                  reason. Where possible and practicable, we will give you reasonable prior notice
                  of an inaccessible or inoperable period;
                </BodyText>
                or external factors.
              </OrderedList>
            </BodyText>
            <BodyText>
              <strong>b.</strong> the Application may evolve, which means we may apply changes to,
              replace or discontinue (temporarily or permanently) any parts, functions or features
              of the Application at any time in our sole discretion. Where possible and practicable,
              we will give you reasonable prior notice of planned changes to, replacement of or
              discontinuation of any parts, functions or features of the Application;
            </BodyText>
            <BodyText>
              <strong>c.</strong> you are solely responsible for all interactions you have with the
              Derive Protocol, including any transactions, executions or transfers associated with
              your crypto assets, and you bear all risk associated with any such interactions;
            </BodyText>
            <BodyText>
              <strong>d.</strong> we have no control over, or liability for, the delivery, quality,
              safety, legality or any other aspect of your interactions related to the Derive
              Protocol and we are not responsible for ensuring such matters with any person or
              entity with whom you may interact through the Derive Protocol;
            </BodyText>
            <BodyText>
              <strong>e.</strong> we have no control over any blockchain or any crypto assets and
              cannot ensure that any of your interactions will be confirmed on the relevant
              blockchain and we do not have the ability to effect any cancellation or modification
              requests regarding any of your interactions with the Derive Protocol;
            </BodyText>
            <BodyText>
              <strong>f.</strong> we do not act as your agent and to the extent permitted by
              Applicable Law, we owe no fiduciary duties or liabilities to you or any other person,
              and that to the extent any such duties or liability may exist at law or in equity, you
              irrevocably disclaim, waive and eliminate those duties and liabilities;
            </BodyText>
            <BodyText>
              <strong>g.</strong> you are solely responsible for complying with all Applicable Laws
              relating to your use of the Application and Derive Protocol;
            </BodyText>
            <BodyText>
              <strong>h.</strong> we have no obligation to inform you of any potential liabilities
              or violations of Applicable Law that may arise in connection with your access and use
              of the Application or the Derive Protocol;
            </BodyText>
            <BodyText>
              <strong>i.</strong> our operation of the Application does not represent any offer,
              solicitation, advice or recommendation to use the Derive Protocol;
            </BodyText>
            <BodyText>
              <strong>j.</strong> we accept no responsibility whatsoever for, and will in no
              circumstances be liable to you in connection with, your use of the Derive Protocol or
              entering into transactions, including crypto asset option or derivative transactions;
            </BodyText>
            <BodyText>
              <strong>k.</strong> the Application may not always be entirely accurate, complete or
              current and may also include technical inaccuracies or typographical errors;
            </BodyText>
            <BodyText>
              <strong>l.</strong> all information provided in connection with your access and use of
              the Application is for informational purposes only and should not be construed as
              professional advice. You should not take, or refrain from taking, any action based on
              any information contained in the Application or any other information that we make
              available at any time, including, without limitation, blog posts, articles, links to
              third-party content, news feeds, tutorials, tweets and videos. Before you make any
              financial, legal, or other decisions involving the Application, you should seek
              independent professionaladvice from an individual who is licensed and qualified in the
              area for which such advice would be appropriate;
            </BodyText>
          </OrderedList>
        </BodyText>
      </Section.YStack>
      <Section.Subheader title="3.3 Acknowledgements – crypto assets" />
      <Section.YStack>
        <BodyText>
          You acknowledge, understand, and agree that:
          <OrderedList>
            <BodyText>
              <strong>a.</strong> There are inherent risks associated with crypto assets and the
              underlying technologies, including cryptography and blockchain, and you agree that we
              are not responsible for any losses or damages associated with these risks.
            </BodyText>
            <BodyText>
              <strong>b.</strong> Interactions with the Derive Protocol, including entering into
              crypto asset options and derivatives, may carry financial risk. Such arrangements are
              highly experimental, risky, and volatile.
            </BodyText>
          </OrderedList>
        </BodyText>
      </Section.YStack>
    </Section>
  )
}

const SectionFour = () => {
  return (
    <Section isLarge>
      <Section.Header title="4. Your Use of the Application" />
      <Section.Subheader title="4.1 Your Obligations" />
      <Section.YStack>
        <BodyText>When accessing and using the Application, you must:</BodyText>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> Comply with these Terms;
          </BodyText>
          <BodyText>
            <strong>b.</strong> Only interact with the Application on your own behalf or on behalf
            of a company that has validly authorised you to interact on its behalf;
          </BodyText>
          <BodyText>
            <strong>c.</strong> Only deal in crypto assets which are legally obtained and belong to
            you;
          </BodyText>
          <BodyText>
            <strong>d.</strong> Comply with all Applicable Laws in connection with using the
            Application and Derive Protocol;
          </BodyText>
          <BodyText>
            <strong>e.</strong> Ensure that at all times:
            <OrderedList>
              <BodyText>
                <i>i.</i> The information that you provide to us, the Application, or the Derive
                Protocol is accurate, complete, and current; and
              </BodyText>
              <BodyText>
                <i>ii.</i> You maintain the security and confidentiality of your private keys
                associated with your wallet address, passwords, API keys, private keys, and other
                credentials; and
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>f.</strong> Comply with all reasonable obligations imposed by us and notified to
            you from time to time.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="4.2 Prohibitions" />
      <Section.YStack>
        <BodyText>When accessing and using the Application you must not:</BodyText>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> Breach these Terms or any Applicable Law;
          </BodyText>
          <BodyText>
            <strong>b.</strong> Engage in any improper or abusive trading practices, including:
            <OrderedList>
              <BodyText>
                <i>i.</i> Any fraudulent act or scheme to defraud, deceive, trick, or mislead;
              </BodyText>
              <BodyText>
                <i>ii.</i> Trading ahead of another user or front-running;
              </BodyText>
              <BodyText>
                <i>iii.</i> Fraudulent trading;
              </BodyText>
              <BodyText>
                <i>iv.</i> Accommodation trading;
              </BodyText>
              <BodyText>
                <i>v.</i> Fictitious transactions;
              </BodyText>
              <BodyText>
                <i>vi.</i> Pre-arranged or non-competitive transactions;
              </BodyText>
              <BodyText>
                <i>vii.</i> Cornering or attempted cornering;
              </BodyText>
              <BodyText>
                <i>viii.</i> Violation of any transaction terms;
              </BodyText>
              <BodyText>
                <i>ix.</i> Spoofing;
              </BodyText>
              <BodyText>
                <i>x.</i> Manipulation;
              </BodyText>
              <BodyText>
                <i>xi.</i> Entering into transactions without a net change in either party’s open
                position but a resulting profit to one party and a loss to the other party;
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>c.</strong> Use the Application in any manner that could interfere with,
            disrupt, negatively affect, or inhibit other users from using the Application, or that
            could damage, disable, overburden, or impair the functioning of the Application in any
            manner;
          </BodyText>
          <BodyText>
            <strong>d.</strong> Circumvent any filtering techniques, security measures, or access
            controls implemented on the Application, including through the use of a VPN;
          </BodyText>
          <BodyText>
            <strong>e.</strong> Use any robot, spider, crawler, scraper, or other automated means to
            extract data, or to introduce any malware, virus, trojan horse, worm, logic bomb,
            drop-dead service, backdoor, shutdown mechanism, or other harmful material into the
            Application;
          </BodyText>
          <BodyText>
            <strong>f.</strong> Provide false, inaccurate, or misleading information while using the
            Application, or engage in any activity that operates to defraud us, any user of the
            Application, or any other person;
          </BodyText>
          <BodyText>
            <strong>g.</strong> Use or access the Application to enter into transactions involving
            the transfer of crypto assets that are the direct or indirect proceeds of any criminal
            or fraudulent activity, including terrorism financing or tax evasion;
          </BodyText>
          <BodyText>
            <strong>h.</strong> Use the Application in any way that is, in our sole opinion,
            libelous, defamatory, profane, obscene, pornographic, sexually explicit, indecent, lewd,
            vulgar, suggestive, harassing, stalking, hateful, threatening, offensive,
            discriminatory, bigoted, abusive, inflammatory, fraudulent, deceptive, or otherwise
            objectionable, or likely or intended to incite, threaten, facilitate, promote, or
            encourage hate, racial intolerance, or violent acts against others;
          </BodyText>
          <BodyText>
            <strong>i.</strong> Use the Application from a jurisdiction (including an IP address in
            a jurisdiction) that we have (in our sole and absolute discretion) determined as a
            jurisdiction where the use of the Application is prohibited;
          </BodyText>
          <BodyText>
            <strong>j.</strong> Harass, abuse, or harm another person, including our employees and
            service providers;
          </BodyText>
          <BodyText>
            <strong>k.</strong> Impersonate another user of the Application or otherwise
            misrepresent yourself;
          </BodyText>
          <BodyText>
            <strong>l.</strong> Use the Application in a manner that:
            <OrderedList>
              <BodyText>
                <i>i.</i> Seeks to interfere with or compromise the integrity, security, or proper
                functioning of any computer, server, network, personal device, or other information
                technology system, including but not limited to the deployment of viruses and denial
                of service attacks;
              </BodyText>
              <BodyText>
                <i>ii.</i> Attempts, in any manner, to obtain the private key, password, account, or
                other security information from any other user, including such information about the
                digital wallet; or
              </BodyText>
              <BodyText>
                <i>iii.</i> Decompiles, reverse engineers, or otherwise attempts to obtain the
                source code or underlying ideas or information of or relating to the Application;
              </BodyText>
            </OrderedList>
          </BodyText>

          <BodyText>
            <strong>m.</strong> Use the Application in a manner that contravenes the terms of use
            (if any) associated with the Derive Protocol.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionFive = () => {
  return (
    <Section isLarge>
      <Section.Header title="5. Wallet" />
      <Section.Subheader title="5.1 Creating or connecting a wallet" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a. </strong>You may create an embedded non-custodial cryptocurrency wallet
            (Embedded Wallet) or connect a wallet (Self Hosted Wallet) to access and use the
            Application.
          </BodyText>
          <BodyText>
            <strong>b. </strong>To create an Embedded Wallet you must sign in and create an Embedded
            Wallet account. You are responsible for providing accurate and complete information and
            you warrant that the information that you provide to create your account is your own.
          </BodyText>
          <BodyText>
            <strong>c. </strong>We will expose to you your Embedded Wallet’s private key so that you
            can recover your Embedded Wallet at any time. It is your responsibility to store this
            private key.
          </BodyText>
          <BodyText>
            <strong>d. </strong>The Embedded Wallet provider relies on a 2 of 2 multi party
            computation scheme to secure a wallet.
          </BodyText>
          <BodyText>
            <strong>e. </strong>You will be asked to create an Embedded Wallet password and to
            securely store your unique private key. The private key is split into two pieces
            (Shards), one of which is generated by the Embedded Wallet provider and the other is
            generated directly in your browser, via our SDK and is protected by your Embedded Wallet
            password. We do not hold or store any Shards.
          </BodyText>
          <BodyText>
            <strong>f. </strong>To transact on the Application you must enter your Embedded Wallet
            password to unlock your Embedded Wallet and to provide both Shards.
          </BodyText>

          <BodyText>
            <strong>g. </strong>We will not transact your Embedded Wallet without your express
            instruction. When we execute a transaction on your instruction, we may batch or sequence
            several actions in order to execute the transaction that has been instructed by you.
          </BodyText>

          <BodyText>
            <strong>h. </strong>After a period of inactivity, you will be automatically logged out
            of your Embedded Wallet.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="5.2 Wallet disclaimers" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a. </strong>You are solely responsible for any crypto assets associated with
            your Embedded Wallet or a Self Hosted Wallet. We do not have custody or control over the
            contents of your Embedded Wallet or a Self Hosted Wallet and have no ability to retrieve
            or transfer its contents.
          </BodyText>
          <BodyText>
            <strong>b. </strong>You understand and accept the risks in using self custodial wallets
            (including the risk third party wallet providers may no longer support your wallet). If
            you do not backup your private key (regardless of whether you use an Embedded Wallet or
            Self Hosted Wallet), if your wallet provider stops providing the service you will not be
            able to recover your wallet or assets.
          </BodyText>

          <BodyText>
            <strong>c. </strong>You must keep any passwords, private keys, and access credentials to
            your Embedded Wallet or a Self Hosted Wallet confidential.
          </BodyText>

          <BodyText>
            <strong>d. </strong>If you lose, mishandle, or have your password, private keys, or
            access credentials stolen, or if there is unauthorized access to your Embedded Wallet or
            a Self Hosted Wallet, you may not be able to recover associated crypto assets, we have
            no ability or obligation to assist you to recover such crypto assets, and we are not
            responsible for any associated loss.
          </BodyText>

          <BodyText>
            <strong>e. </strong>We are not responsible for transferring, safeguarding, or
            maintaining any private keys, passwords (including passwords to access private key
            shards), or any crypto assets associated with your Embedded Wallet or a Self Hosted
            Wallet.
          </BodyText>

          <BodyText>
            <strong>f. </strong>We are not responsible for any fraud or loss that is associated with
            your Embedded Wallet or a Self Hosted Wallet.
          </BodyText>

          <BodyText>
            <strong>g. </strong>We have no responsibility or liability to you in the event you are
            unable to access your Embedded Wallet for any reason.
          </BodyText>

          <BodyText>
            <strong>h. </strong>If you connect a Self Hosted Wallet, your relationship with any
            third party provider is governed by the applicable terms of service to that third party.
            We have no involvement in your access to and use of a Self Hosted Wallet.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="5.3 Our responsibilities" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a. </strong> We will provide you with your Shards at onboarding.
          </BodyText>
          <BodyText>
            <strong>b. </strong>We will store limited backup data that may assist you to recover
            your Shards.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionSix = () => {
  return (
    <Section isLarge>
      <Section.Header title="6. Disclaimers" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> We are a software developer. We do not operate Derive Protocol or
            any underlying exchanges, protocols, or decentralised networks through which you can
            interact or engage in crypto asset transactions. We do not have access to your private
            key and cannot initiate an interaction with your crypto assets or otherwise access your
            crypto assets. We are not responsible for any activities that you engage in when using
            your wallet or the Application.
          </BodyText>
          <BodyText>
            <strong>b.</strong> We have no control over the Derive Protocol. We only operate an
            Application through which you can access the Derive Protocol; however, we have no
            oversight or responsibility for any of your interactions with the Derive Protocol or
            other users.
          </BodyText>
          <BodyText>
            <strong>c.</strong> You are responsible for complying with all Applicable Laws that
            govern your use of the Application and Derive Protocol. We have no responsibility for
            the Derive Protocol and whether your interaction with the Derive Protocol complies with
            Applicable Laws in your jurisdiction. You understand and agree to assume full
            responsibility for all of the risks of accessing and using the Application to interact
            with the Derive Protocol.
          </BodyText>
          <BodyText>
            <strong>d.</strong> We make no representations or warranties that the information,
            products, or services provided through our Application are appropriate for access or use
            in other jurisdictions. You are not permitted to access or use our Application in any
            jurisdiction or country if it would be contrary to Applicable Law. We reserve the right
            to limit the availability of our Application to any person, geographic area, or
            jurisdiction at any time and at our sole and absolute discretion.
          </BodyText>
          <BodyText>
            <strong>e.</strong> You understand and agree that your use of the Application is at your
            sole risk. Subject to the operation of any Consumer Guarantee, we disclaim all
            representations and warranties, express, implied, or statutory, and with respect to the
            Application and the code (proprietary or open source), we disclaim any representation or
            warranty, express, implied, or statutory, including without limitation any
            representations or warranties of title, non-infringement, merchantability, usage,
            security, suitability, or fitness for any particular purpose, or as to the workmanship
            or technical coding thereof, or the absence of any defects therein, whether latent or
            patent. We do not represent or warrant that the Application, code, and any related
            information are accurate, complete, reliable, current, or error-free. The Application is
            provided on an “as is” and “as available” basis without warranties of any kind, either
            express or implied, including without limitation implied warranties of merchantability,
            fitness for a particular purpose, or non- infringement. You acknowledge that no advice,
            information, or statement that we make should be treated as creating any warranty
            concerning the Application. We do not endorse, guarantee, or assume responsibility for
            any advertisements, offers, or statements made by third parties concerning the
            Application.
          </BodyText>
          <BodyText>
            <strong>f.</strong> We may connect you with third parties to create a non-custodial
            digital wallet or allow you to connect your own non-custodial digital wallet to access
            the Application. Your relationship with any third party provider is governed by the
            applicable terms of service. We do not operate and are not responsible for any
            non-custodial digital wallets. By creating or connecting a non-custodial digital wallet
            you acknowledge and agree that:
            <OrderedList>
              <BodyText>
                <i>i.</i> We are not responsible for transferring, safeguarding, or maintaining your
                cryptographic private keys, passwords (including passwords to access private key
                shards), or any crypto assets associated with your digital wallet;
              </BodyText>
              <BodyText>
                <i>ii.</i> We do not have custody or control over the contents of your wallet and
                have no ability to retrieve or transfer its contents;
              </BodyText>
              <BodyText>
                <i>iii.</i> You understand and accept the risks in using third-party providers
                (including the risk that such third parties may no longer support your wallet) and
                that you are solely responsible for controlling access to your private keys and
                passwords. We do not have access to your private keys or any passwords;
              </BodyText>
              <BodyText>
                <i>iv.</i> You should never share your wallet credentials, private key, password, or
                seed phrase with anyone; and
              </BodyText>
              <BodyText>
                <i>v.</i> If you lose, mishandle, or have your private keys or password stolen, or
                if there is unauthorized access to your wallet, you may not be able to recover
                associated crypto assets, we have no ability or obligation to assist you to recover
                such crypto assets, and we are not responsible for any associated loss.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>g.</strong> By accessing and using the Application, you represent that you
            understand:
            <OrderedList>
              <BodyText>
                <i>i.</i> The Application facilitates access to the Derive Protocol, the use of
                which has many inherent risks;
              </BodyText>
              <BodyText>
                <i>ii.</i> The cryptographic and blockchain-based systems have inherent risks to
                which you are exposed when using the Application;
              </BodyText>
              <BodyText>
                <i>iii.</i> And have a working knowledge of the usage and intricacies of blockchain-
                based crypto assets;
              </BodyText>
              <BodyText>
                <i>iv.</i> That the markets for blockchain-based crypto assets and projects are
                highly volatile due to factors that include but are not limited to adoption,
                speculation, technology, security, and regulation; and
              </BodyText>
              <BodyText>
                <i>v.</i> That the cost and speed of transacting with blockchain-based systems are
                variable and may increase or decrease, respectively, drastically at any time.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>h.</strong> You acknowledge and agree that we are not responsible for any of
            these variables or risks associated with the Derive Protocol and cannot be held liable
            for any resulting losses that you experience while accessing or using the Application.
          </BodyText>
          <BodyText>
            <strong>i.</strong> The Application may contain references or links to third-party
            resources, including but not limited to information, materials, products, or services,
            that we do not own or control. Third parties may also offer promotions related to your
            access and use of the Application. We do not endorse or assume any responsibility for
            any such resources or promotions. If you access any such resources or participate in any
            such promotions, you do so at your own risk, and you understand that the Terms do not
            apply to your dealings or relationships with any third parties. You expressly relieve us
            of any and all liability arising from your use of any such resources or participation in
            any such promotions.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionSeven = () => {
  return (
    <Section isLarge>
      <Section.Header title="7. Your Information" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> We may require you to provide certain information in connection with
            your use of the Application. The only information we collect is your blockchain wallet
            address, completed transaction hashes, and user interactions with the Interface. We do
            not collect Personal Information unless required under Applicable Law or with your prior
            consent.
          </BodyText>
          <BodyText>
            <strong>b.</strong> We use your information to:
            <OrderedList>
              <BodyText>
                <i>i.</i> Make improvements to the Application;
              </BodyText>
              <BodyText>
                <i>ii.</i> Detect, prevent, and mitigate financial crime and other illicit or
                harmful activities on the Application;
              </BodyText>
              <BodyText>
                <i>iii.</i> Comply with our risk management processes and compliance obligations
                (both internal processes to manage risks associated with the Application and under
                Applicable Law); and
              </BodyText>
              <BodyText>
                <i>iv.</i> Enhance your experience with the Application and provide ancillary
                services associated with the Application.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>c.</strong> We may disclose your information with certain service providers to
            help us promote the safety, security, and integrity of the Application (including for
            the purposes set out in this section 6). We do not retain any information for longer
            than necessary.
          </BodyText>
          <BodyText>
            <strong>d.</strong> We use third party service providers who may receive or
            independently obtain your information (which may include Personal Information) from
            publicly available sources. We do not control how these third parties handle your
            information and you should review their privacy policies (as applicable) to understand
            how they collect, use, and disclose your information.
          </BodyText>
          <BodyText>
            <strong>e.</strong> You acknowledge that using the Application to interact with the
            Derive Protocol will result in you interacting with the Ethereum blockchain and Layer-2
            rollups, which makes your transactions public. We do not control and are not responsible
            for any information you make public on the Ethereum blockchain.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionEight = () => {
  return (
    <Section>
      <Section.Header title="8. Proprietary Rights" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> You acknowledge that certain aspects of the Application may use,
            incorporate, or link to certain open source components and that your use of the
            Application is subject to and you will comply with any applicable open source licences
            that govern any such open source components (collectively, Open Source Licences).
            Without limiting the scope of our ownership and rights in relation to the Open Source
            Licences, you may not:
            <OrderedList>
              <BodyText>
                <i>i.</i> Resell, lease, lend, share, distribute, or otherwise permit any third
                party to use the Application; or
              </BodyText>
              <BodyText>
                <i>ii.</i> Otherwise use the Application in a manner that violates the Open Source
                Licences.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>b.</strong> Excluding third-party software that the Application incorporates, as
            between you and us, we own the Application, including all technology, content, and other
            materials used, displayed, or provided on the Application (including all intellectual
            property rights subsisting therein, whether or not subject to the Open Source Licences),
            and hereby grant you a limited, non-exclusive, revocable, non-transferable,
            non-sublicensable licence to access and use those portions of the Application that are
            proprietary to us and not available pursuant to the Open Source Licences.
          </BodyText>
          <BodyText>
            <strong>c.</strong> Any of our product or service names, logos, and other marks used on
            the Application are trademarks owned by us or our licensors. You must not copy, imitate,
            or use them without our prior written consent or the prior written consent of the
            applicable licensor, and these Terms do not grant you any rights in those trademarks.
            You must not remove, obscure, or alter any legal notices displayed in or along with the
            Application.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionNine = () => {
  return (
    <Section isLarge>
      <Section.Header title="9. Indemnification and Limitation of Liability" />
      <Section.Subheader title="9.1 Indemnification" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> You indemnify and hold harmless us and our officers, directors,
            employees, contractors, agents, affiliates, and subsidiaries (Indemnified Parties) from
            and against all claims, damages, obligations, losses, liabilities, costs, and expenses
            arising out of or relating to:
            <OrderedList>
              <BodyText>
                <i>i.</i> Your access to and use of the Application or the Derive Protocol;
              </BodyText>
              <BodyText>
                <i>ii.</i> Crypto assets associated with your digital wallet;
              </BodyText>
              <BodyText>
                <i>iii.</i> Your breach of these Terms, the right of any third party, or any
                Applicable Law;
              </BodyText>
              <BodyText>
                <i>iv.</i> Any other party’s access and use of the Application or Derive Protocol
                with your assistance or using any device or account that you own or control.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>b.</strong> If you are obliged to indemnify any Indemnified Party, we (or at our
            sole discretion, the applicable Indemnified Party) will have the right, in our or its
            sole discretion, to control any action or proceeding and to determine whether we wish to
            settle, and if so, on what terms, and you agree to cooperate with us in the defence.
          </BodyText>
          <BodyText>
            <strong>c.</strong> Your liability under this clause 9.1 will be proportionately reduced
            to the extent that we caused or contributed to the relevant claim, damage, obligation,
            loss, liability, cost, or expense.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="9.2 Limitation of Liability" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> Our liability is limited to providing you with access to the
            Application. To the maximum extent (and subject to our liability under any Consumer
            Guarantee) and to the extent that our liability is not otherwise limited by this part or
            any other term of these Terms, in no event will we be liable for Consequential Loss
            (whether the claim is in relation to loss or damage arising under statute, in contract,
            or in tort (including negligence) or otherwise) arising out of or in connection with:
            <OrderedList>
              <BodyText>
                <i>i.</i> Any access to or use of the Application or the Derive Protocol;
              </BodyText>
              <BodyText>
                <i>ii.</i> Any hacking, tampering, or other unauthorized access to or use of the
                Application or Derive Protocol;
              </BodyText>
              <BodyText>
                <i>iii.</i> Any access to or use of any information obtained by any unauthorized
                access to or use of the Application or Derive Protocol; or
              </BodyText>
              <BodyText>
                <i>iv.</i> These Terms.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>b.</strong> To the maximum extent permitted by Applicable Law (and subject to
            our liability under any Consumer Guarantee that may apply), in no event will we be
            liable to you for any loss or damage of any kind (including Consequential Loss) arising
            out of or in connection with:
            <OrderedList>
              <BodyText>
                <i>i.</i> Your use of the Application or Derive Protocol;
              </BodyText>
              <BodyText>
                <i>ii.</i> Any errors, mistakes, or inaccuracies of content in the Application or
                Derive Protocol;
              </BodyText>
              <BodyText>
                <i>iii.</i> Personal injury or property damage of any nature whatsoever resulting
                from any access to or use of the Application or Derive Protocol;
              </BodyText>
              <BodyText>
                <i>iv.</i> Unauthorized access to or use of any secure server or database in our
                control, or the use of any information or data stored therein;
              </BodyText>
              <BodyText>
                <i>v.</i> Interruption or cessation of function related to the Application or Derive
                Protocol;
              </BodyText>
              <BodyText>
                <i>vi.</i> Bugs, viruses, trojan horses, or the like that may be transmitted to or
                through the Application or Derive Protocol;
              </BodyText>
              <BodyText>
                <i>vii.</i> Errors or omissions in, or loss or damage incurred as a result of, the
                use of any content made available through the Application or Derive Protocol;
              </BodyText>
              <BodyText>
                <i>viii.</i> Defamatory, offensive, or illegal conduct of any third party; or
              </BodyText>
              <BodyText>
                <i>ix.</i> Your need to modify your practices, content, or behavior, or your loss of
                or inability to do business, as a result of changes to these Terms.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>c.</strong> To the extent our liability is not otherwise limited by this part
            and to the maximum extent permitted by Applicable Law, we are not liable for
            Consequential Loss.
          </BodyText>
          <BodyText>
            <strong>d.</strong> To the maximum extent permitted by Applicable Law, our liability for
            any breach or non-compliance with a Consumer Guarantee is limited to one or more of the
            following (at our election):
            <OrderedList>
              <BodyText>
                <i>i.</i> In the case of goods, their replacement, the supply of equivalent goods,
                their repair, or payment of the cost of doing any of those things; or
              </BodyText>
              <BodyText>
                <i>ii.</i> In the case of services, to resupplying the services or payment of the
                cost of their supply.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>e.</strong> You agree that any instructions you provide to us through your use
            of the Application are your responsibility and must be accurate, correct, and complete
            in all respects. We are not liable for any loss or claim arising from our actions or
            inactions in accordance with your instructions.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionTen = () => {
  return (
    <Section isLarge>
      <Section.Header title="10. Disabling, Modifying, and Restricting Your Access" />
      <Section.YStack>
        <BodyText>
          We may disable, modify, or restrict your access to the Application (for example,
          restricting certain features) at any time in the event of any breach of these Terms,
          including if we reasonably believe any of your representations or warranties may be untrue
          or inaccurate, and we will not be liable to you for any losses or damages you may suffer
          as a result of or in connection with the Application or the Derive Protocol being
          inaccessible to you at any time or for any reason.
        </BodyText>
      </Section.YStack>
    </Section>
  )
}

const SectionEleven = () => {
  return (
    <Section>
      <Section.Header title="11. Arbitration and Class Action Waiver" />
      <Section.Subheader title="11.1 Binding Arbitration" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> Except for disputes in which either party seeks to bring an
            individual action in small claims court or seeks injunctive or other equitable relief
            for the alleged unlawful use of intellectual property, each party waives:
            <OrderedList>
              <BodyText>
                <i>i.</i> The right to have any and all disputes or claims arising from these Terms,
                your use or access to the Application, or any other disputes with us (collectively
                Disputes) resolved in a court; and
              </BodyText>
              <BodyText>
                <i>ii.</i> Any right to a jury trial.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>b.</strong> The parties agree to arbitrate Disputes that are not resolved
            informally through binding arbitration instead of having the Dispute decided by a judge
            or jury in court.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="11.2 No Class Arbitrations, Class Actions, or Representative Actions" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> The parties agree that any Dispute is personal to them and that any
            such Dispute will be resolved solely through individual arbitration and will not be
            brought as a class arbitration, class action, or any other type of representative
            proceeding.
          </BodyText>
          <BodyText>
            <strong>b.</strong> Neither party agrees to class arbitration or to an arbitration in
            which an individual attempts to resolve a Dispute as a representative of another
            individual or group of individuals.
          </BodyText>
          <BodyText>
            <strong>c.</strong> The parties agree that a Dispute cannot be brought as a class or
            other types of representative action, whether within or outside of arbitration, or on
            behalf of any other individual or group of individuals.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="11.3 Process" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> The parties agree to notify the other in writing of any Dispute
            within thirty (30) days of when it arises so that the parties can attempt in good faith
            to resolve the Dispute informally.
          </BodyText>
          <BodyText>
            <strong>b.</strong> Notice to us shall be provided by sending us an email at
            support@derive.xyz.
          </BodyText>
          <BodyText>
            <strong>c.</strong> Your notice must include:
            <OrderedList>
              <BodyText>
                <i>i.</i> Your name, postal address, and email address;
              </BodyText>
              <BodyText>
                <i>ii.</i> A description of the nature or basis of the Dispute; and
              </BodyText>
              <BodyText>
                <i>iii.</i> The specific action that you are seeking.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>d.</strong> If the parties cannot resolve the Dispute within thirty (30) days of
            us receiving the notice, either you or we may commence an arbitration proceeding.
          </BodyText>
          <BodyText>
            <strong>e.</strong> The parties agree that any arbitration or claim must be commenced or
            filed within one (1) year after the Dispute arose. If no arbitration or claim has
            commenced or been filed in this time period, the parties agree that the claim is
            permanently barred.
          </BodyText>
          <BodyText>
            <strong>f.</strong> The arbitration will be conducted in accordance with the UNICTRAL
            Arbitration Rules. The appointing authority and place of arbitration shall be nominated
            by us as notified to you.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="11.4 Authority of Arbitrator" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> As limited by these Terms and applicable arbitration rules, the
            arbitrator will have:
            <OrderedList>
              <BodyText>
                i. the exclusive authority and jurisdiction to make all procedural and substantive
                decisions regarding a Dispute; and
              </BodyText>
              <BodyText>
                ii. the authority to grant any remedy that would otherwise be available in court.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>b.</strong> The arbitrator may only conduct an individual arbitration and may
            not consolidate more than one individual claim, preside over any type of class or
            representative proceeding or preside over any proceeding involving more than one
            individual.
          </BodyText>
        </OrderedList>
      </Section.YStack>
    </Section>
  )
}

const SectionTwelve = () => {
  return (
    <Section isLarge>
      <Section.Header title="12. General" />
      <Section.Subheader title="12.1 Changes to these Terms" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> We may amend, modify, update, and change any of the terms and
            conditions contained in these Terms in our reasonable discretion from time to time.
          </BodyText>
          <BodyText>
            <strong>b.</strong> We will endeavour to give you at least ten (10) days prior notice of
            any change that is likely to materially affect or disrupt the manner in which you use
            the Application, except in circumstances where such a change is due to a change by a
            third party provider assisting in the operation of the Application and we were not aware
            of the change in advance. In these circumstances, we will endeavour to notify you in a
            reasonable time period after becoming aware of the change.
          </BodyText>
          <BodyText>
            <strong>c.</strong> Any subsequent access, viewing, or use of the Application will
            constitute your acceptance of any new or modified terms. If you do not agree with any
            new or modified terms, you must immediately cease using the Application.
          </BodyText>
        </OrderedList>
      </Section.YStack>
      <Section.Subheader title="12.2 Choice of Law (Governing law)" />

      <Section.YStack>
        <BodyText>
          These Terms and any Dispute will be governed by and construed in accordance with the laws
          of Panama.
        </BodyText>
      </Section.YStack>

      <Section.Subheader title="12.3 Subcontracting" />
      <Section.YStack>
        <BodyText>
          We may subcontract (including to any of our affiliates) the whole or any part of our
          obligations under these Terms without your prior written consent.
        </BodyText>
      </Section.YStack>

      <Section.Subheader title="12.4 Taxes" />
      <Section.YStack>
        <BodyText>
          It is your responsibility to determine whether and to what extent any taxes apply to any
          interactions you make through the Application and to without collect, report, and remit
          the correct amounts of taxes to the applicable tax authorities.
        </BodyText>
      </Section.YStack>

      <Section.Subheader title="12.5 Assignment" />
      <Section.YStack>
        <BodyText>
          We may assign our rights and obligations either in whole or in part under these Terms.
          Your rights and obligations are personal to you and therefore non-assignable.
        </BodyText>
      </Section.YStack>

      <Section.Subheader title="12.6 Invalidity" />
      <Section.YStack>
        <OrderedList>
          <BodyText>
            <strong>a.</strong> If a provision of these Terms or a right or remedy of a party under
            these Terms is invalid or unenforceable in a particular jurisdiction:
            <OrderedList>
              <BodyText>
                <i>i.</i> It is read down or severed in that jurisdiction only to the extent of the
                invalidity or unenforceability; and
              </BodyText>
              <BodyText>
                <i>ii.</i> It does not affect the invalidity or enforceability of that provision in
                another jurisdiction or the remaining provisions in any jurisdiction.
              </BodyText>
            </OrderedList>
          </BodyText>
          <BodyText>
            <strong>b.</strong> This clause 12.6 is not limited by any other provision of these
            Terms in relation to severability, prohibition, or enforceability.
          </BodyText>
        </OrderedList>
      </Section.YStack>

      <Section.Subheader title="12.7 Survival of Indemnities" />
      <Section.YStack>
        <BodyText>
          Each indemnity contained in these Terms is an additional, separate, and independent
          obligation, and no one indemnity limits the generality of another indemnity.
        </BodyText>
      </Section.YStack>

      <Section.Subheader title="12.8 Severability" />
      <Section.YStack>
        <BodyText>
          Any term of these Terms which is wholly or partially void or unenforceable is severed to
          the extent that it is void or unenforceable. The validity or enforceability of the
          remainder of these Terms is not affected.
        </BodyText>
      </Section.YStack>
      <Section.Subheader title="12.9 Force Majeure" />
      <Section.YStack>
        <BodyText>
          We shall not be liable for delays, failure in performance, or interruption of service
          which results directly or indirectly from any cause or condition beyond its reasonable
          control, including but not limited to any delay or failure due to any act of God, act of
          civil or military authorities, act of terrorists, civil disturbance, war, strike or other
          labor dispute, fire, interruption in telecommunications or internet services or network
          provider services, failure of equipment or software, other catastrophe or any other
          occurrence which is beyond our reasonable control and shall not affect the validity and
          enforceability of any remaining provisions.
        </BodyText>
      </Section.YStack>
      <Section.Subheader title="12.10 Enforcement of Our Rights" />
      <Section.YStack>
        <BodyText>
          We may not always strictly enforce our rights under these Terms. If we do choose not to
          enforce our rights at any time, this is a temporary measure and we may enforce our rights
          strictly again at any time.
        </BodyText>
      </Section.YStack>
      <Section.Subheader title="12.11 Survival" />
      <Section.YStack>
        <BodyText>
          All provisions contained in these Terms which by their nature extend beyond the expiration
          or termination of these Terms will continue to be binding and operate after the
          termination or expiration of these Terms.
        </BodyText>
      </Section.YStack>
    </Section>
  )
}

const SectionThirteen = () => {
  return (
    <Section isLarge>
      <Section.Header title="13. Definitions and Interpretation" />
      <Section.Subheader title="13.1 Definitions" />
      <Section.YStack>
        <BodyText>The following definitions apply in these Terms:</BodyText>
        <BodyText>
          <strong>Applicable Law</strong> means any domestic or foreign law, rule, statute,
          regulation, by-law, order, protocol, code, decree, or another directive, requirement, or
          guideline, published or in-force that applies to or is otherwise intended to govern or
          regulate any person, property, transaction, activity, event, or other matter, including
          any rule, order, judgment, directive, or other requirement or guideline issued by any
          domestic or foreign federal, provincial, or state, municipal, local, or other
          governmental, regulatory, judicial, or administrative authority having jurisdiction over
          us, you, the Application, or the Derive Protocol, or as otherwise duly enacted,
          enforceable by law, common law, or equity.
        </BodyText>
        <BodyText>
          <strong>Application</strong> has the meaning given to that term in clause 1(b).
        </BodyText>
        <BodyText>
          <strong>Consequential Loss</strong> means any loss, damage, or costs incurred by you that
          is indirect or consequential as well as loss of revenue; loss of income; loss of business;
          loss of profits; loss of production; loss of or damage to goodwill or credit; loss of
          business reputation, future reputation, or publicity; loss of use; loss of interest;
          losses arising from claims by third parties; loss of or damage to credit rating; loss of
          anticipated savings and/or loss or denial of opportunity.
        </BodyText>
        <BodyText>
          <strong>Consumer Guarantee</strong> means any right you may have under consumer protection
          laws against providers of goods or services.
        </BodyText>
        <BodyText>
          <strong>Disputes</strong> has the meaning given to that term in clause 10.1(a).
        </BodyText>
        <BodyText>
          <strong>Indemnified Parties</strong> has the meaning given to that term in clause 9.1(a).
        </BodyText>
        <BodyText>
          <strong>Derive DAO</strong> has the meaning given to that term in clause 1(a).
        </BodyText>
        <BodyText>
          <strong>Derive Protocol</strong> has the meaning given to that term in clause 1(a).
        </BodyText>
        <BodyText>
          <strong>Open Source Licence</strong> has the meaning given to that term in clause 7(a).
        </BodyText>
        <BodyText>
          <strong>Personal Information</strong> has the meaning assigned to the terms “personal
          information” or “personally identifiable information” or similar under Applicable Law and
          will at a minimum mean any information whether true or not and whether recorded in a
          material form or not relating to a natural person whose identity is apparent or can
          reasonably be ascertained from the information or any combination of them.
        </BodyText>
        <BodyText>
          <strong>Restricted Person</strong> means a person, entity, organization, company, trust,
          partnership, estate, resident, citizen, or agent of or incorporated in, governed by, or
          established under the laws of Australia, the United States of America, Canada, Panama,
          Iran, Iraq, Libya, Mali, Nicaragua, Cuba, Democratic People’s Republic of Korea (North
          Korea), Russia, Somalia, Sudan, Syria, Yemen, Zimbabwe, Myanmar (Burma), Cote
          D&apos;Ivoire (Ivory Coast), Democratic Republic of Congo, the regions of Crimea, Donetsk,
          or Luhansk, or any other country or region that is notified by us as a restricted region
          from time to time.
        </BodyText>
        <BodyText>
          <strong>Self Hosted Third Party Wallet</strong> has the meaning given to that term in
          clause 5.1(a).
        </BodyText>
        <BodyText>
          <strong>Shards</strong> has the meaning given to that term in clause 5.1(c).
        </BodyText>
        <BodyText>
          <strong>Sanctioned Person</strong> means a person, resident, citizen, or agent of, or
          incorporated in, any jurisdiction which is listed on any economic or trade restrictions,
          sanctions, or embargo list, or specifically designated persons lists published by
          international organizations as well as any state and governmental authorities of any
          jurisdiction, including but not limited to the lists of United Nations, European Union and
          its Member States, United States of America, United Kingdom, and Australia sanctions
          lists.
        </BodyText>
        <BodyText>
          <strong>Terms</strong> has the meaning given to that term in clause 1(c).
        </BodyText>
        <BodyText>
          <strong>VPN</strong> means virtual private network.
        </BodyText>
        <BodyText>
          <strong>we, us, or our</strong> has the meaning given to those terms in clause 1(d).
        </BodyText>
        <BodyText>
          <strong>you, your, or user(s)</strong> has the meaning given to those terms in clause
          1(d).
        </BodyText>
      </Section.YStack>
      <Section.Subheader title="13.2 Interpretation" />
      <Section.YStack>
        <BodyText>
          In these Terms, the following rules of interpretation apply unless the contrary intention
          appears:
        </BodyText>
        <BodyText>
          <strong>a.</strong> Headings are for convenience only and do not affect the interpretation
          of these Terms;
        </BodyText>
        <BodyText>
          <strong>b.</strong> The singular includes the plural and vice versa;
        </BodyText>
        <BodyText>
          <strong>c.</strong> Words that are gender neutral or gender specific include each gender;
        </BodyText>
        <BodyText>
          <strong>d.</strong> Where a word or phrase is given a particular meaning, other parts of
          speech and grammatical forms of that word or phrase have corresponding meanings;
        </BodyText>
        <BodyText>
          <strong>e.</strong> The words ’such as’, ’including’, ’particularly’, and similar
          expressions are not words of limitation;
        </BodyText>
        <BodyText>
          <strong>f.</strong> A reference to:
          <OrderedList>
            <BodyText>
              <strong>i.</strong> a person includes a natural person, partnership, joint venture,
              government agency, association, corporation, trust, or other corporate body;
            </BodyText>
            <BodyText>
              <strong>ii.</strong> a thing (including but not limited to a chose in action or other
              right) includes a part of that thing;
            </BodyText>
            <BodyText>
              <strong>iii.</strong> a party includes its agents, successors and permitted assigns;
            </BodyText>
            <BodyText>
              <strong>iv.</strong> a document includes all amendments or supplements to that
              document;
            </BodyText>
            <BodyText>
              <strong>v.</strong> a clause, term, party, schedule or attachment is a reference to a
              clause or term of, or party, schedule or attachment to these Terms;
            </BodyText>
            <BodyText>
              <strong>vi.</strong> these Terms include all schedules and attachments to it;
            </BodyText>
            <BodyText>
              <strong>vii.</strong> a law includes a constitutional provision, treaty, decree,
              convention, statute, regulation, ordinance, by-law, judgment, rule of common law or
              equity and is a reference to that law as amended, consolidated or replaced;
            </BodyText>
            <BodyText>
              <strong>viii.</strong> a statute includes any regulation, ordinance, by-law or other
              subordinate legislation under it;
            </BodyText>
            <BodyText>
              <strong>ix.</strong> an agreement other than these Terms includes an undertaking, or
              legally enforceable arrangement or understanding whether or not in writing; and
            </BodyText>
            <BodyText>
              <strong>x.</strong> a monetary amount is in Australian dollars and all amounts payable
              under or in connection with these Terms are payable in Australian dollars;
            </BodyText>
          </OrderedList>
        </BodyText>

        <BodyText>
          <strong>g.</strong> No rule of construction applies to the disadvantage of a party because
          that party was responsible for the preparation of these Terms or any part of it;
        </BodyText>
        <BodyText>
          <strong>h.</strong> In determining the time of day where relevant to these Terms, the
          relevant time of day is:
        </BodyText>
        <BodyText>
          <strong>i.</strong> For the purposes of giving or receiving notices, the time of day where
          a party receiving a notice is located; or
        </BodyText>
        <BodyText>
          <strong>j.</strong> For any other purpose under these Terms, the time of day in the place
          where the party required to perform an obligation is located;
        </BodyText>

        <BodyText>
          <strong>k.</strong> A day is the period of time commencing at midnight and ending
          immediately before the next midnight is to occur;
        </BodyText>
        <BodyText>
          <strong>l.</strong> If a period of time is calculated from a particular day, act, or event
          (such as the giving of a notice), unless otherwise stated in these Terms, it is to be
          calculated exclusive of that day or the day of that act or event.
        </BodyText>
      </Section.YStack>
    </Section>
  )
}

export default function TermsOfUsePage() {
  return (
    <PageWithBanner
      bannerTitle="Terms of Use"
      bannerSubtitle={`Updated ${formatDate(UPDATED_TIMESTAMP)}`}
    >
      <SectionOne />
      <SectionTwo />
      <SectionThree />
      <SectionFour />
      <SectionFive />
      <SectionSix />
      <SectionSeven />
      <SectionEight />
      <SectionNine />
      <SectionTen />
      <SectionEleven />
      <SectionTwelve />
      <SectionThirteen />
    </PageWithBanner>
  )
}
